/**
 * "Nice" axis ticks — the round numbers a person would actually write on an
 * axis (0, 2500, 5000, ...), not whatever `max / count` happens to divide
 * out to. Standard algorithm: pick a step from {1, 2, 2.5, 5} scaled by a
 * power of ten, choosing the smallest one at or above `max / count` so the
 * axis ends up with at most `count` intervals. This module returns numbers
 * only — no SVG, no Svelte, no formatting of the numbers into strings.
 *
 * Per src/lib/calc/numbers.ts: pure, never throws, never returns a
 * non-finite number.
 */

const STEP_MULTIPLES = [1, 2, 2.5, 5, 10];

/** Safety net against a pathological step producing a runaway loop below. */
const MAX_TICKS = 1000;

/**
 * Ticks from 0 up to the first one at or above `max`, using a step chosen
 * so `max / step` lands at or just under `count` (default 4).
 *
 * `max <= 0` (a flat or empty axis) returns `[0]` — one tick, not an empty
 * axis and not a division by zero from `max / count`.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const wantedCount = Number.isFinite(count) && count > 0 ? count : 4;
  const step = niceStep(max / wantedCount);

  const ticks: number[] = [];
  for (let i = 0; i <= MAX_TICKS; i++) {
    const tick = r3(i * step);
    ticks.push(tick);
    if (tick >= max) break;
  }
  return ticks;
}

/** The smallest of {1, 2, 2.5, 5} x 10^n that is at or above `rawStep`. */
function niceStep(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  for (const multiple of STEP_MULTIPLES) {
    const candidate = multiple * magnitude;
    if (candidate >= rawStep) return candidate;
  }
  return 10 * magnitude;
}

function r3(value: number): number {
  return Math.round(value * 1000) / 1000;
}
